'use client';

// Third Party
import { c as _c } from "react/compiler-runtime";
import * as React from 'react';

// Utils
import { ota_info } from './utils';
import { isSuccessfulComparisonQuote } from './types';

// Components
import { LoadingSkeleton, ComparisonQuoteItem, SavingsBanner, SAVINGS_TOOLTIP_COPY } from './components';
import { useQuoteComparison } from './quote-comparison-context';
import { AnalyticsRef } from '@components/analytics-ref';
import { useQuotes } from '../quotes-context';
import { IconTooltip } from '@components/icon-tooltip';
import { IconNames } from '@components/icon';
import cx from 'classnames';
export const QuotesCompare: React.FC = () => {
  const $ = _c(36);
  const {
    comparisonQuotes,
    isLoading,
    showComparison,
    quoteUrls,
    afterTaxes
  } = useQuoteComparison();
  const {
    quoteData
  } = useQuotes();
  const savvyPriceCents = quoteData?.bookingQuoteByListingAndDates.__typename === "BookingQuote" ? afterTaxes ? quoteData.bookingQuoteByListingAndDates.totalRecoverableCostCents : quoteData.bookingQuoteByListingAndDates.totalWithoutTaxesCents : undefined;
  let t0;
  bb0: {
    if (!savvyPriceCents) {
      t0 = null;
      break bb0;
    }
    let t1;
    if ($[0] !== savvyPriceCents) {
      t1 = {
        externalSource: "savvy",
        priceCents: savvyPriceCents,
        checkInDate: "",
        checkOutDate: "",
        externalListingId: "",
        numberOfAdults: 0,
        numberOfChildren: 0,
        numberOfInfants: 0,
        numberOfPets: 0,
        __typename: "ComparisonQuote" as const
      };
      $[0] = savvyPriceCents;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    t0 = t1;
  }
  const savvyQuote = t0;
  let prices;
  if ($[2] !== comparisonQuotes || $[3] !== savvyPriceCents) {
    prices = [];
    if (savvyPriceCents !== undefined) {
      prices.push(savvyPriceCents);
    }
    comparisonQuotes.forEach(quote => {
      if (isSuccessfulComparisonQuote(quote) && quote.priceCents !== undefined) {
        prices.push(quote.priceCents);
      }
    });
    $[2] = comparisonQuotes;
    $[3] = savvyPriceCents;
    $[4] = prices;
  } else {
    prices = $[4];
  }
  const minPriceCents = prices.length > 0 ? Math.min(...prices) : undefined;
  let t1;
  bb1: {
    if (minPriceCents === undefined) {
      t1 = false;
      break bb1;
    }
    let pricesAtMin;
    if ($[5] !== comparisonQuotes || $[6] !== minPriceCents || $[7] !== savvyPriceCents) {
      pricesAtMin = [];
      if (savvyPriceCents === minPriceCents) {
        pricesAtMin.push(savvyPriceCents);
      }
      comparisonQuotes.forEach(quote_0 => {
        if (isSuccessfulComparisonQuote(quote_0) && quote_0.priceCents === minPriceCents) {
          pricesAtMin.push(quote_0.priceCents);
        }
      });
      $[5] = comparisonQuotes;
      $[6] = minPriceCents;
      $[7] = savvyPriceCents;
      $[8] = pricesAtMin;
    } else {
      pricesAtMin = $[8];
    }
    t1 = pricesAtMin.length === 1;
  }
  const isMinPriceUnique = t1;
  let otaPrices;
  if ($[9] !== comparisonQuotes) {
    otaPrices = [];
    comparisonQuotes.forEach(quote_1 => {
      if (isSuccessfulComparisonQuote(quote_1) && quote_1.priceCents !== undefined && quote_1.externalSource !== "savvy") {
        otaPrices.push(quote_1.priceCents);
      }
    });
    $[9] = comparisonQuotes;
    $[10] = otaPrices;
  } else {
    otaPrices = $[10];
  }
  const minOtaPriceCents = otaPrices.length > 0 ? Math.min(...otaPrices) : undefined;
  let t2;
  bb2: {
    if (savvyPriceCents === undefined || minOtaPriceCents === undefined || savvyPriceCents >= minOtaPriceCents) {
      t2 = undefined;
      break bb2;
    }
    t2 = minOtaPriceCents - savvyPriceCents;
  }
  const savingsAmountCents = t2;
  const hasOtherComparisonQuotes = comparisonQuotes.some(_temp);
  const shouldShowSavingsBanner = !isLoading && showComparison && savingsAmountCents !== undefined && savingsAmountCents > 0;
  const [isVisible, setIsVisible] = React.useState(false);
  let t3;
  let t4;
  if ($[11] !== isLoading || $[12] !== showComparison) {
    t3 = () => {
      if (!isLoading && showComparison) {
        const timer = setTimeout(() => setIsVisible(true), 50);
        return () => clearTimeout(timer);
      }
      setIsVisible(false);
    };
    t4 = [isLoading, showComparison];
    $[11] = isLoading;
    $[12] = showComparison;
    $[13] = t3;
    $[14] = t4;
  } else {
    t3 = $[13];
    t4 = $[14];
  }
  React.useEffect(t3, t4);
  if (!hasOtherComparisonQuotes && !isLoading) {
    let t5;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <div className="flex items-center justify-center text-[16px]">📣</div>;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    let t6;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div className="flex-1 flex items-center justify-center"><span className="text-[14px] font-light text-center text-gray-dark">Book direct pricing, no platform markups</span></div>;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    let t7;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <AnalyticsRef name="price_comparison_tool"><div className="overflow-hidden max-h-screen"><div className="w-full"><div className="rounded-xl border border-gray-light overflow-hidden"><div className="flex items-center bg-secondary-xlight p-2"><div className="flex items-center justify-between w-full space-x-1 px-2">{t5}{t6}<div className="flex items-center justify-center mt-0.5"><IconTooltip icon={IconNames.QUESTION_CIRCLE} placement="top">{SAVINGS_TOOLTIP_COPY}</IconTooltip></div></div></div></div></div></div></AnalyticsRef>;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    return t7;
  }
  let t5;
  if ($[18] !== isLoading) {
    t5 = isLoading && <LoadingSkeleton />;
    $[18] = isLoading;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== afterTaxes || $[21] !== comparisonQuotes || $[22] !== isLoading || $[23] !== isMinPriceUnique || $[24] !== isVisible || $[25] !== minPriceCents || $[26] !== quoteUrls || $[27] !== savingsAmountCents || $[28] !== savvyPriceCents || $[29] !== savvyQuote || $[30] !== shouldShowSavingsBanner || $[31] !== showComparison) {
    t6 = showComparison && !isLoading && <div className={`grid rounded-xl border border-gray-light overflow-hidden transition-all duration-700 ease-in-out ${isVisible ? "opacity-100 grid-rows-[1fr]" : "opacity-0 grid-rows-[0fr]"}`}><div className="min-h-0 flex flex-col">{shouldShowSavingsBanner && <SavingsBanner savingsAmountCents={savingsAmountCents} />}<div className={cx("flex gap-2 w-full", !shouldShowSavingsBanner && "py-2")}>{savvyQuote && <><ComparisonQuoteItem afterTaxes={afterTaxes} key="savvy" comparisonQuote={savvyQuote} otaUrl="" otaInfo={ota_info} isSavvy={true} isBestDeal={minPriceCents !== undefined && savvyPriceCents === minPriceCents && isMinPriceUnique} /></>}{comparisonQuotes.filter(_temp2).filter(_temp3).filter(_temp4).map((quote_6, index) => {
            const quotePriceCents = isSuccessfulComparisonQuote(quote_6) ? quote_6.priceCents : undefined;
            const isQuoteBestDeal = minPriceCents !== undefined && quotePriceCents !== undefined && quotePriceCents === minPriceCents && isMinPriceUnique;
            return <React.Fragment key={index}>{index < comparisonQuotes.length - 1 && <div className="border-l border-gray-light my-2" />}<ComparisonQuoteItem afterTaxes={afterTaxes} comparisonQuote={quote_6} otaUrl={quoteUrls[quote_6.externalSource || ""]} otaInfo={ota_info} isBestDeal={isQuoteBestDeal} /></React.Fragment>;
          })}</div></div></div>;
    $[20] = afterTaxes;
    $[21] = comparisonQuotes;
    $[22] = isLoading;
    $[23] = isMinPriceUnique;
    $[24] = isVisible;
    $[25] = minPriceCents;
    $[26] = quoteUrls;
    $[27] = savingsAmountCents;
    $[28] = savvyPriceCents;
    $[29] = savvyQuote;
    $[30] = shouldShowSavingsBanner;
    $[31] = showComparison;
    $[32] = t6;
  } else {
    t6 = $[32];
  }
  let t7;
  if ($[33] !== t5 || $[34] !== t6) {
    t7 = <AnalyticsRef name="price_comparison_tool"><div className="overflow-hidden max-h-screen"><div className="w-full ">{t5}{t6}</div></div></AnalyticsRef>;
    $[33] = t5;
    $[34] = t6;
    $[35] = t7;
  } else {
    t7 = $[35];
  }
  return t7;
};
function _temp(quote_2) {
  return quote_2.externalSource !== "savvy" && isSuccessfulComparisonQuote(quote_2) && quote_2.priceCents !== undefined;
}
function _temp2(quote_3) {
  return quote_3.externalSource !== "savvy";
}
function _temp3(quote_4) {
  return isSuccessfulComparisonQuote(quote_4);
}
function _temp4(quote_5) {
  return quote_5.priceCents !== undefined;
}